import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  ArrowLeft, 
  Send, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  MoreVertical,
  User as UserIcon,
  ShieldAlert,
  Calendar,
  Zap
} from "lucide-react";
import { useTickets } from "@/hooks/useTickets";
import { useAdmin } from "@/hooks/useAdmin";
import { Ticket, TicketMessage } from "@/types/admin";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function AdminTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getTicket, getTicketMessages, updateTicket, sendReply, loading: ticketLoading } = useTickets();
  const { admin } = useAdmin();
  
  type TicketWithRelations = Ticket & {
    user?: {
      name: string;
      avatar_url?: string;
      subscription_tier?: string;
    };
    assignee?: {
      name: string;
      avatar_url?: string;
    };
  };
  const [ticket, setTicket] = useState<TicketWithRelations | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fetchTicketData = useCallback(async () => {
    if (!id) return;
    const ticketData = await getTicket(id);
    if (ticketData) {
      setTicket(ticketData);
      const msgData = await getTicketMessages(id);
      setMessages(msgData);
    }
  }, [id, getTicket, getTicketMessages]);

  useEffect(() => {
    fetchTicketData();
  }, [fetchTicketData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendReply = async () => {
    if (!newMessage.trim() || !id || !admin) return;
    
    setSending(true);
    const success = await sendReply(id, admin.id, newMessage);
    if (success) {
      setNewMessage("");
      await fetchTicketData();
    }
    setSending(false);
  };

  const handleStatusChange = async (newStatus: Ticket["status"]) => {
    if (!id) return;
    await updateTicket(id, { status: newStatus });
    await fetchTicketData();
  };

  if (ticketLoading && !ticket) {
    return (
      <div className="flex h-full items-center justify-center p-12">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-black border-t-transparent" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-12 text-center">
        <h2 className="text-2xl font-black">{t('admin.ticketDetail.notFound')}</h2>
        <button onClick={() => navigate('/admin/tickets')} className="mt-4 text-[#4DA6FF] font-bold underline">
          {t('common.backToList')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8 h-[calc(100vh-64px)] flex flex-col">
      <div className="mb-6 flex items-center justify-between">
        <button 
          onClick={() => navigate('/admin/tickets')}
          className="flex items-center gap-2 rounded-lg border-2 border-black bg-white px-4 py-2 font-black shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px] transition-all"
        >
          <ArrowLeft className="h-5 w-5" />
          {t('common.backToList')}
        </button>

        <div className="flex items-center gap-3">
          <select 
            value={ticket.status}
            onChange={(e) => handleStatusChange(e.target.value as Ticket["status"])}
            className="rounded-lg border-2 border-black px-4 py-2 font-black shadow-[2px_2px_0_#000] focus:outline-none"
          >
            <option value="open">{t('admin.tickets.status.open')}</option>
            <option value="in_progress">{t('admin.tickets.status.in_progress')}</option>
            <option value="resolved">{t('admin.tickets.status.resolved')}</option>
            <option value="closed">{t('admin.tickets.status.closed')}</option>
          </select>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-8 overflow-hidden lg:grid-cols-4">
        {/* Main Conversation Area */}
        <div className="lg:col-span-3 flex flex-col rounded-2xl border-2 border-black bg-[#F5F0E8] shadow-[8px_8px_0_#000] overflow-hidden">
          {/* Ticket Header In-Chat */}
          <div className="border-b-2 border-black bg-white p-6">
            <div className="mb-2 flex items-center gap-2">
              <span className={cn(
                "rounded-full border-2 border-black px-3 py-0.5 text-xs font-black uppercase tracking-wider",
                ticket.priority === 'urgent' ? 'bg-red-500 text-white' : 'bg-[#FFD93D]'
              )}>
                {t(`admin.tickets.priority.${ticket.priority}`)}
              </span>
              <span className="text-sm font-black text-gray-400 capitalize">{t(`admin.tickets.type.${ticket.type}`)}</span>
            </div>
            <h1 className="text-2xl font-black tracking-tight">{ticket.subject}</h1>
          </div>

          {/* Messages Thread */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Initial Message */}
            <div className="flex gap-4">
              <div className="h-10 w-10 flex-shrink-0 rounded-lg border-2 border-black bg-[#6FEDD6] flex items-center justify-center">
                <UserIcon className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-black">{ticket.name}</span>
                  <span className="text-xs text-gray-500 font-medium">{new Date(ticket.created_at).toLocaleString()}</span>
                </div>
                <div className="rounded-xl rounded-tl-none border-2 border-black bg-white p-4 font-medium shadow-[4px_4px_0_#000]">
                  {ticket.message}
                </div>
              </div>
            </div>

            {/* Conversation Messages */}
            {messages.map((msg) => (
              <div key={msg.id} className={cn("flex gap-4", msg.is_admin && "flex-row-reverse")}>
                <div className={cn(
                  "h-10 w-10 flex-shrink-0 rounded-lg border-2 border-black flex items-center justify-center",
                  msg.is_admin ? "bg-[#FF6B9D]" : "bg-[#6FEDD6]"
                )}>
                  {msg.is_admin ? <ShieldAlert className="h-6 w-6 text-white" /> : <UserIcon className="h-6 w-6" />}
                </div>
                <div className={cn("flex-1 max-w-[80%]", msg.is_admin && "text-right")}>
                  <div className={cn("flex items-center gap-2 mb-1", msg.is_admin && "flex-row-reverse")}>
                    <span className="font-black">{msg.is_admin ? t('admin.ticketDetail.adminTeam') : (ticket.user?.name || ticket.name)}</span>
                    <span className="text-xs text-gray-500 font-medium">{new Date(msg.created_at).toLocaleString()}</span>
                  </div>
                  <div className={cn(
                    "rounded-xl border-2 border-black p-4 font-medium shadow-[4px_4px_0_#000]",
                    msg.is_admin ? "rounded-tr-none bg-[#FF6B9D] text-white" : "rounded-tl-none bg-white"
                  )}>
                    {msg.message}
                  </div>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Reply Area */}
          <div className="border-t-2 border-black bg-white p-6">
            <div className="flex gap-4">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('admin.ticketDetail.replyPlaceholder')}
                className="flex-1 min-h-[100px] rounded-xl border-2 border-black p-4 font-bold focus:shadow-[4px_4px_0_#000] focus:outline-none transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.metaKey) handleSendReply();
                }}
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button 
                onClick={handleSendReply}
                disabled={sending || !newMessage.trim()}
                className="flex items-center gap-2 rounded-xl border-2 border-black bg-[#6FEDD6] px-8 py-3 text-lg font-black shadow-[4px_4px_0_#000] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] disabled:opacity-50 disabled:translate-x-0 disabled:translate-y-0 disabled:shadow-[4px_4px_0_#000] transition-all"
              >
                {sending ? t('admin.ticketDetail.sending') : (
                  <>
                    <Send className="h-5 w-5" />
                    {t('admin.ticketDetail.sendReply')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6">
          <div className="rounded-2xl border-2 border-black bg-white p-6 shadow-[8px_8px_0_#000]">
            <h3 className="mb-4 text-sm font-black uppercase tracking-wider text-black/40">{t('admin.ticketDetail.userInfo')}</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full border-2 border-black bg-[#FFD93D] flex items-center justify-center font-black text-xl overflow-hidden">
                  {ticket.user?.avatar_url ? (
                    <img src={ticket.user.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : ticket.name.charAt(0)}
                </div>
                <div>
                  <div className="font-black leading-tight">{ticket.name}</div>
                  <div className="text-xs text-gray-500 font-bold">{ticket.email}</div>
                </div>
              </div>

              <div className="space-y-2 border-t-2 border-black/5 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-gray-400">{t('admin.ticketDetail.subscriptionPlan')}</span>
                  <span className="inline-flex items-center gap-1 rounded border-2 border-black bg-[#6FEDD6] px-2 py-0.5 text-xs font-black">
                    <Zap className="h-3 w-3" />
                    {(ticket.user?.subscription_tier || 'free').toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black text-gray-400">{t('admin.ticketDetail.createdAt')}</span>
                  <span className="text-xs font-bold">{new Date(ticket.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border-2 border-black bg-white p-6 shadow-[8px_8px_0_#000]">
            <h3 className="mb-4 text-sm font-black uppercase tracking-wider text-black/40">{t('admin.ticketDetail.assignedTo')}</h3>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full border-2 border-black bg-gray-100 flex items-center justify-center">
                <UserIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="text-sm font-bold text-gray-500">{t('admin.ticketDetail.unassigned')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
